import { defineStore } from "pinia";

import moment from "moment";

import PresenceService from "@/api/services/newPresencia.service";
import { useAuthStore } from "@/app/stores/authStore";

// Store de fichaje/Trackernet. Incluye timers y Worker, por eso no debe
// persistirse: son referencias vivas que solo tienen sentido en la sesión actual.
export const usePresenceStore = defineStore("presence", {
  state: () => ({
    sign_button: true,
    stop_button: true,
    hoursWorked: "",
    hoursBreaked: "",
    currentSign: [],
    currentStop: [],
    reportPresence: [],
    reportDateRange: [],
    reportFilters: null,
    entryDuration: 0,
    // Referencias no serializables usadas por el contador en vivo.
    intervalId: null,
    isEntryActive: false,
    changedDayTrackernet: false,
    workerTimeEntry: null,
  }),
  getters: {
    getStateButtonSign: (state) => state.sign_button,
    getStateButtonStop: (state) => state.stop_button,
    getHoursWorked: (state) => state.hoursWorked,
    getHoursBreaked: (state) => state.hoursBreaked,
    getReportPresence: (state) => state.reportPresence,
    getDateRangeReport: (state) => state.reportDateRange,
  },
  actions: {
    setStatusSignButton() {
      this.sign_button = !this.sign_button;
    },
    setSignButton(value) {
      this.sign_button = value;
    },
    setStatusStopButton() {
      this.stop_button = !this.stop_button;
    },
    setStopButton(value) {
      this.stop_button = value;
    },
    setHoursWorked(value) {
      this.hoursWorked = value;
    },
    setHoursBreaked(value) {
      this.hoursBreaked = value;
    },
    setCurrentSign(value) {
      this.currentSign = value;
    },
    setCurrentStop(value) {
      this.currentStop = value;
    },
    setReportPresence(array) {
      this.reportPresence = array;
    },
    resetReportPresence() {
      this.reportPresence = [];
    },
    setDateRangeReport(array) {
      this.reportDateRange = array;
    },
    setReportFilters(filters) {
      this.reportFilters = { ...filters };
    },
    setEntryDuration(duration) {
      this.entryDuration = duration;
    },
    incrementDuration() {
      this.entryDuration += 1;
    },
    setIntervalId(id) {
      this.intervalId = id;
    },
    clearIntervalId() {
      this.intervalId = null;
    },
    setIsEntryActive(isActive) {
      this.isEntryActive = isActive;
    },
    clearIsEntryActive() {
      this.isEntryActive = false;
    },
    setChangedDayTrackernet(value) {
      this.changedDayTrackernet = value;
    },
    setWorkerTimeEntry(workerInstance) {
      this.workerTimeEntry = workerInstance;
    },
    async addSigning() {
      const authStore = useAuthStore();
      const user = authStore.user;

      // Reproduce el flujo Vuex anterior: alterna entry/exit según el último
      // segmento abierto que haya en currentSign.
      const sendSign = async () => {
        const params = {};
        params.employee = user["@id"];
        const lastPresence = this.currentSign[this.currentSign.length - 1];
        let type = "entry";
        if (lastPresence && !lastPresence.out) {
          type = "exit";
        }
        params.type = type;

        if (type === "entry") {
          return PresenceService.startWorkRecordsUser(params).then(() => true);
        }

        if (type === "exit") {
          return PresenceService.endWorkRecordsUser(params).then(() => true);
        }

        return false;
      };

      return await sendSign();
    },
    async addStop(payload) {
      // Alterna pause/resume según la última pausa abierta.
      const sendSign = async () => {
        let params = {};
        if (payload && payload.pauseReason) {
          params = payload;
        }
        const lastPresence = this.currentStop[this.currentStop.length - 1];

        let type = "stop";
        if (lastPresence && !lastPresence.resume) {
          type = "resume";
        }

        if (type === "stop") {
          return PresenceService.pauseWorkRecordsUser(params).then(() => true);
        }

        if (type === "resume") {
          return PresenceService.resumeWorkRecordsUser(params).then(() => true);
        }

        return false;
      };

      return await sendSign();
    },
    calculateWorkedTime(now) {
      let sum = 0;
      let breakSum = 0;
      this.currentSign
        .filter((v) => v.segmentType === "work")
        .forEach((v) => {
          if (v.out) {
            sum += moment(v.out, "HHmmss").diff(moment(v.in, "HHmmss"));
          } else {
            sum += moment.duration(now.diff(moment(v.in, "HHmmss"))).asMilliseconds();
          }
        });
      this.currentSign
        .filter((v) => v.segmentType === "pause" && v.reason === "break")
        .forEach((v) => {
          if (v.end) {
            breakSum += moment(v.end, "HHmmss").diff(moment(v.start, "HHmmss"));
          } else {
            breakSum += moment.duration(now.diff(moment(v.start, "HHmmss"))).asMilliseconds();
          }
        });
      this.hoursWorked = sum;
      this.hoursBreaked = breakSum;
    },
    currentPresence(signings) {
      if (!signings || signings.length === 0) {
        this.setCurrentSign([]);
        this.setCurrentStop([]);
        this.setHoursWorked(0);
        this.setHoursBreaked(0);
        return;
      }

      // Normaliza la lista plana de fichajes del backend en segmentos de trabajo
      // y pausa para que los componentes no repitan esta reconstrucción.
      const allSegments = [];
      const currentStop = [];
      let openWork = null;
      let openPause = null;

      signings.forEach((elmt) => {
        if (elmt.type === "entry" || elmt.type === "resume") {
          if (elmt.type === "resume" && openPause) {
            openPause.end = elmt.time;
            if (currentStop.length > 0) {
              currentStop[currentStop.length - 1].resume = elmt.time;
            }
            openPause = null;
          }
          openWork = { segmentType: "work", inType: elmt.type, in: elmt.time };
          allSegments.push(openWork);
        } else if (elmt.type === "pause") {
          if (openWork) {
            openWork.outType = "pause";
            openWork.out = elmt.time;
            openWork.outReason = elmt.pauseReason;
            openWork = null;
          }
          openPause = {
            segmentType: "pause", start: elmt.time, reason: elmt.pauseReason, otherText: elmt.pauseReasonOtherText || null,
          };
          currentStop.push({ pause: elmt.time });
          allSegments.push(openPause);
        } else if (elmt.type === "exit" && openWork) {
          openWork.outType = "exit";
          openWork.out = elmt.time;
          openWork = null;
        }
      });

      this.setCurrentSign(allSegments);
      this.setCurrentStop(currentStop);
    },
    setStatusButtons(status) {
      this.setSignButton(status.canStart);
      this.setStopButton(!status.canResume);
    },
    startInterval() {
      if (window.Worker) {
        // El Worker mantiene el contador estable incluso cuando el hilo principal
        // está ocupado renderizando o resolviendo peticiones.
        const workerTimeEntry = new Worker(
          new URL("@/worker/timeEntryWorker.js", import.meta.url),
          { type: "module" },
        );
        workerTimeEntry.onmessage = (event) => {
          if (event.data === "tick") {
            this.incrementDuration();
          }
        };
        workerTimeEntry.postMessage({ command: "start", interval: 1000 });
        this.setWorkerTimeEntry(workerTimeEntry);
      } else {
        console.error("Web Workers no son compatibles en este navegador.");
      }
      if (this.isEntryActive) return;
      this.setIsEntryActive(true);
    },
    stopInterval() {
      if (this.workerTimeEntry && this.workerTimeEntry.postMessage) {
        this.workerTimeEntry.postMessage({ command: "stop" });
        this.workerTimeEntry.terminate();
      }
      this.setWorkerTimeEntry(null);
      this.setEntryDuration(0);
      this.setIsEntryActive(false);
    },
  },
});
